import { useEffect, useRef, useState } from 'react';
import Link from '@/components/common/AccessLink';
import {
  Boxes,
  Check,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  MessageCircle,
  PackageCheck,
  PackageOpen,
  Printer,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Truck,
  X,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { useDeliveries } from '@/hooks/useDeliveries';
import { useDebounce } from '@/hooks/useDebounce';
import PageHeader from '@/components/common/PageHeader';
import ErrorState from '@/components/common/ErrorState';
import { Input, Select } from '@/components/ui/FormControls';
import SelectedSalesActionDock from '@/components/sales/SelectedSalesActionDock';
import DeliveryDocuments from '@/components/delivery/DeliveryDocuments';
import DeliveryShipmentRow from '@/components/delivery/DeliveryShipmentRow';
import DeliveryUpdateDialog, { deliveryButton } from '@/components/delivery/DeliveryUpdateDialog';
import DeliveryWhatsAppDialog from '@/components/delivery/DeliveryWhatsAppDialog';
import {
  deliveryLabel,
  deliveryStages,
  deliveryRpc,
  type DeliveryRow,
  type DeliveryOutcome,
} from '@/lib/deliveries';

const deliveryViews = [
  ['active', 'Active shipments'],
  ['completed', 'Delivered'],
  ['cancelled', 'Cancelled'],
  ['all', 'All shipments'],
  ['needs_method', 'Needs delivery method'],
] as const;

export default function DeliveriesPage() {
  const { organization, profile, access } = useAuth();
  return <DeliveryWorkspace key={`${organization?.id}:${profile?.id}:${access?.access_version}`} />;
}
function DeliveryWorkspace() {
  const { can } = useAuth();
  const [view, setView] = useState('active');
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState('');
  const [method, setMethod] = useState('');
  const [issue, setIssue] = useState('');
  const [carrier, setCarrier] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const pageCheckbox = useRef<HTMLInputElement>(null);
  const debounced = useDebounce(search, 300);
  const carrierQuery = useDebounce(carrier, 300);
  const filters = {
    view,
    search: debounced,
    stage,
    method,
    issue,
    carrier: carrierQuery,
    from,
    to,
    offset: page * 50,
    limit: 50,
  };
  const query = useDeliveries(filters);
  const [selected, setSelected] = useState<DeliveryRow[]>([]);
  const [dialog, setDialog] = useState<'update' | 'whatsapp' | null>(null);
  const [frozen, setFrozen] = useState<DeliveryRow[]>([]);
  const [selecting, setSelecting] = useState(false);
  const [error, setError] = useState('');
  const selectionFlight = useRef(false);
  const anchor = useRef<HTMLDivElement>(null);
  const allRequest = useRef<AbortController | null>(null);
  const identity = JSON.stringify({
    view,
    search: debounced,
    stage,
    method,
    issue,
    carrier: carrierQuery,
    from,
    to,
  });
  useEffect(() => {
    setSelected([]);
    setPage(0);
    setError('');
    allRequest.current?.abort();
  }, [identity]);
  useEffect(() => () => allRequest.current?.abort(), []);
  const rows = query.data?.rows || [];
  const toggle = (row: DeliveryRow) =>
    setSelected((current) =>
      current.some((r) => r.sale_id === row.sale_id)
        ? current.filter((r) => r.sale_id !== row.sale_id)
        : current.length < 100
          ? [...current, row]
          : current,
    );
  const pageSelected =
    rows.length > 0 && rows.every((r) => selected.some((s) => s.sale_id === r.sale_id));
  useEffect(() => {
    if (pageCheckbox.current)
      pageCheckbox.current.indeterminate =
        !pageSelected && rows.some((row) => selected.some((item) => item.sale_id === row.sale_id));
  }, [rows, selected, pageSelected]);
  function togglePage() {
    setError('');
    if (!pageSelected && new Set([...selected, ...rows].map((r) => r.sale_id)).size > 100) {
      setError('Select up to 100 deliveries. Clear some selections or narrow the filters.');
      return;
    }
    setSelected((current) =>
      pageSelected
        ? current.filter((s) => !rows.some((r) => r.sale_id === s.sale_id))
        : [...current.filter((s) => !rows.some((r) => r.sale_id === s.sale_id)), ...rows],
    );
  }
  async function allMatching() {
    if (selectionFlight.current) return;
    selectionFlight.current = true;
    setSelecting(true);
    setError('');
    const request = new AbortController();
    allRequest.current = request;
    try {
      const result = await deliveryRpc<{ requires_narrowing: boolean; rows: DeliveryRow[] }>(
        'prepare_delivery_selection',
        { p_filters: filters },
        request.signal,
      );
      if (request.signal.aborted) return;
      if (result.requires_narrowing)
        setError('More than 100 deliveries match. Narrow the filters or select a page at a time.');
      else setSelected(result.rows);
    } catch (e) {
      if (!request.signal.aborted) setError(e instanceof Error ? e.message : 'Selection failed.');
    } finally {
      selectionFlight.current = false;
      setSelecting(false);
    }
  }
  function complete(results: DeliveryOutcome[]) {
    setSelected((current) =>
      current.filter(
        (row) =>
          !results.some(
            (r) => r.sale_id === row.sale_id && ['updated', 'unchanged'].includes(r.outcome),
          ),
      ),
    );
  }
  const activeFilters = [
    stage && { key: 'stage', label: deliveryLabel(stage), clear: () => setStage('') },
    method && { key: 'method', label: deliveryLabel(method), clear: () => setMethod('') },
    issue && {
      key: 'issue',
      label: issue === 'any' ? 'Needs attention' : deliveryLabel(issue),
      clear: () => setIssue(''),
    },
    carrier && { key: 'carrier', label: carrier, clear: () => setCarrier('') },
    from && { key: 'from', label: `From ${from}`, clear: () => setFrom('') },
    to && { key: 'to', label: `To ${to}`, clear: () => setTo('') },
  ].filter((value): value is { key: string; label: string; clear: () => void } => !!value);
  const resetFilters = () => {
    setSearch('');
    setStage('');
    setMethod('');
    setIssue('');
    setCarrier('');
    setFrom('');
    setTo('');
  };
  const counts = query.data?.status_counts;
  const inTransit =
    (counts?.dispatched || 0) + (counts?.in_transit || 0) + (counts?.out_for_delivery || 0);
  const pageStart = query.data?.total ? page * 50 + 1 : 0;
  const pageEnd = Math.min((page + 1) * 50, query.data?.total || 0);
  const open = (name: 'update' | 'whatsapp', row?: DeliveryRow) => {
    setFrozen(row ? [{ ...row }] : selected.map((r) => ({ ...r })));
    setDialog(name);
  };
  return (
    <div ref={anchor} className={selected.length ? 'space-y-5 pb-48 sm:pb-32' : 'space-y-5'}>
      <PageHeader
        title="Delivery"
        description="Your shipment workspace. Pack, send and keep customers updated."
        actions={
          <>
            <Link className={deliveryButton} to="/print-templates">
              <Printer className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only sm:not-sr-only">Print templates</span>
            </Link>
            <button
              className={deliveryButton}
              disabled={query.isFetching}
              onClick={() => void query.refetch()}
            >
              <RefreshCw
                className={`h-4 w-4 ${query.isFetching ? 'animate-spin motion-reduce:animate-none' : ''}`}
                aria-hidden="true"
              />
              <span className="sr-only sm:not-sr-only">Refresh</span>
            </button>
          </>
        }
      />

      {query.data && !query.error && view !== 'needs_method' && (
        <section
          aria-label="Current filtered shipment counts"
          className="grid grid-cols-3 gap-2 sm:gap-4"
        >
          {(
            [
              {
                label: 'Matching',
                value: query.data.total,
                icon: Boxes,
                color: 'text-cyan-700 dark:text-primary',
                note: 'Current filters',
              },
              {
                label: 'To pack',
                value: counts?.booked || 0,
                icon: PackageOpen,
                color: 'text-violet-700 dark:text-violet-300',
                note: 'Booked shipments',
              },
              {
                label: 'In transit',
                value: inTransit,
                icon: Truck,
                color: 'text-cyan-700 dark:text-cyan-300',
                note: 'Dispatched & on the way',
              },
            ] as const
          ).map((metric) => (
            <div
              key={metric.label}
              className="rounded-2xl border border-border bg-card px-3 py-3 sm:p-4"
            >
              <div className="flex items-center justify-between gap-1">
                <p className="whitespace-nowrap text-xs font-medium text-muted-foreground">
                  {metric.label}
                </p>
                <metric.icon
                  className={`hidden h-4 w-4 shrink-0 sm:block ${metric.color}`}
                  aria-hidden="true"
                />
              </div>
              <p className="mt-2 text-2xl font-semibold tabular-nums tracking-tight sm:text-3xl">
                {metric.value}
              </p>
              <p className="mt-1 hidden text-[11px] text-muted-foreground sm:block">
                {metric.note}
              </p>
            </div>
          ))}
        </section>
      )}

      <section
        aria-label="Shipment workspace"
        className="min-w-0 overflow-hidden rounded-2xl border border-border bg-card"
      >
        <div
          role="group"
          aria-label="Delivery views"
          className="hidden overflow-x-auto border-b border-border bg-muted/20 px-2 sm:flex sm:px-4"
        >
          {deliveryViews.map(([value, label]) => (
            <button
              key={value}
              aria-pressed={view === value}
              className={`relative min-h-12 shrink-0 whitespace-nowrap px-3 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:px-4 sm:text-sm ${view === value ? 'text-cyan-700 dark:text-primary' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => setView(value)}
            >
              {label}
              {view === value && (
                <span
                  aria-hidden="true"
                  className="absolute inset-x-3 bottom-0 h-0.5 rounded-t-full bg-primary"
                />
              )}
            </button>
          ))}
        </div>
        <div className="border-b border-border bg-muted/20 p-3 sm:hidden">
          <label className="block text-xs font-medium text-muted-foreground">
            <span className="sr-only">Delivery view</span>
            <Select
              className="w-full"
              value={view}
              onChange={(event) => setView(event.target.value)}
            >
              {deliveryViews.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </label>
        </div>
        <div className="space-y-3 p-3 sm:p-4">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <label className="relative min-w-0 flex-1">
              <span className="sr-only">Search deliveries</span>
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute left-3.5 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`min-h-11 pl-10 ${search ? 'pr-12' : 'pr-3'}`}
                placeholder="Search orders, customers, tracking…"
              />
              {search && (
                <button
                  type="button"
                  className="absolute right-0 top-0 z-[1] flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground"
                  aria-label="Clear delivery search"
                  onClick={() => setSearch('')}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </label>
            <button
              type="button"
              aria-expanded={filtersOpen}
              aria-controls="delivery-more-filters"
              onClick={() => setFiltersOpen(!filtersOpen)}
              className={`${deliveryButton} shrink-0 ${filtersOpen || activeFilters.length ? 'border-primary/30 bg-primary/5 text-cyan-700 dark:text-primary' : ''}`}
            >
              <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
              <span>Filters</span>
              {activeFilters.length > 0 && (
                <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] tabular-nums">
                  {activeFilters.length}
                </span>
              )}
            </button>
          </div>
          <div
            id="delivery-more-filters"
            hidden={!filtersOpen}
            className="space-y-4 rounded-xl border border-border bg-muted/20 p-3 sm:p-4"
          >
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <label className="space-y-1.5 text-xs font-medium text-muted-foreground">
                Stage
                <Select
                  className="mt-1.5 w-full"
                  value={stage}
                  onChange={(e) => setStage(e.target.value)}
                >
                  <option value="">All stages</option>
                  {deliveryStages.map((s) => (
                    <option key={s} value={s}>
                      {deliveryLabel(s)}
                    </option>
                  ))}
                </Select>
              </label>
              <label className="space-y-1.5 text-xs font-medium text-muted-foreground">
                Delivery method
                <Select
                  className="mt-1.5 w-full"
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                >
                  <option value="">All methods</option>
                  <option value="courier">Courier</option>
                  <option value="local_delivery">Local delivery</option>
                </Select>
              </label>
              <label className="space-y-1.5 text-xs font-medium text-muted-foreground">
                Delivery issues
                <Select
                  className="mt-1.5 w-full"
                  value={issue}
                  onChange={(e) => setIssue(e.target.value)}
                >
                  <option value="">All shipments</option>
                  <option value="any">Needs attention</option>
                  <option value="delay">Delay</option>
                  <option value="failed_attempt">Failed attempt</option>
                  <option value="return_to_origin">Return to origin</option>
                  <option value="other">Other issue</option>
                </Select>
              </label>
              <label className="space-y-1.5 text-xs font-medium text-muted-foreground">
                Courier company
                <Input
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                  placeholder="Company name"
                />
              </label>
              <label className="space-y-1.5 text-xs font-medium text-muted-foreground">
                Sale date from
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </label>
              <label className="space-y-1.5 text-xs font-medium text-muted-foreground">
                Sale date to
                <Input
                  type="date"
                  value={to}
                  min={from || undefined}
                  onChange={(e) => setTo(e.target.value)}
                />
              </label>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-2">
              <p className="text-xs text-muted-foreground">
                Counts and selection follow these filters.
              </p>
              <button
                type="button"
                className="min-h-11 px-2 text-xs font-semibold text-cyan-700 dark:text-primary"
                onClick={resetFilters}
              >
                Reset filters
              </button>
            </div>
          </div>
          {!!activeFilters.length && (
            <div className="flex flex-wrap items-center gap-2" aria-label="Active delivery filters">
              {activeFilters.map((filter) => (
                <button
                  type="button"
                  key={filter.key}
                  onClick={filter.clear}
                  aria-label={`Remove filter: ${filter.label}`}
                  className="inline-flex min-h-11 max-w-full items-center gap-2 rounded-lg border border-border bg-muted/40 px-2.5 text-xs text-foreground"
                >
                  <span className="truncate">{filter.label}</span>
                  <X className="h-3 w-3 shrink-0" aria-hidden="true" />
                </button>
              ))}
              <button
                type="button"
                onClick={resetFilters}
                className="min-h-11 px-2 text-xs font-medium text-cyan-700 dark:text-primary"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </section>

      {view === 'needs_method' && (
        <div className="flex gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
          <HelpCircle
            className="mt-0.5 h-5 w-5 shrink-0 text-cyan-700 dark:text-primary"
            aria-hidden="true"
          />
          <div>
            <h2 className="text-sm font-semibold">Set up older sales for delivery</h2>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Choose Courier or Local delivery on a sale to add it to your shipment queue. Customer
              pickup stays in Sales.
            </p>
          </div>
        </div>
      )}
      {error && (
        <p
          role="alert"
          className="rounded-xl border border-destructive/20 bg-destructive/5 p-3 text-sm text-destructive"
        >
          {error}
        </p>
      )}
      {query.isPending ? (
        <section aria-label="Loading deliveries" aria-busy="true" className="space-y-3">
          {Array.from({ length: 4 }, (_, i) => (
            <div
              key={i}
              className="h-28 animate-pulse rounded-2xl border border-border bg-muted/40 motion-reduce:animate-none"
            />
          ))}
          <p role="status" className="sr-only">
            Loading deliveries…
          </p>
        </section>
      ) : query.error ? (
        <ErrorState
          title="Deliveries could not be loaded"
          message={query.error.message}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <>
          <section aria-label="Delivery results" aria-busy={query.isFetching} className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
              <label className="flex min-h-11 cursor-pointer items-center gap-2.5 text-xs font-medium sm:text-sm">
                <input
                  ref={pageCheckbox}
                  type="checkbox"
                  className="h-[18px] w-[18px] accent-primary"
                  checked={pageSelected}
                  disabled={!rows.length || selecting}
                  onChange={togglePage}
                />
                Select this page
              </label>
              <div className="flex items-center gap-3 text-xs">
                <span className="hidden text-muted-foreground sm:inline" role="status">
                  {pageStart}–{pageEnd} of {query.data?.total || 0}
                </span>
                <button
                  type="button"
                  disabled={selecting || !query.data?.total}
                  className="min-h-11 font-semibold text-cyan-700 dark:text-primary disabled:opacity-50"
                  onClick={() => void allMatching()}
                >
                  {selecting ? 'Preparing…' : 'Select all matching'}
                </button>
              </div>
            </div>
            {!rows.length ? (
              <div className="flex flex-col items-center rounded-2xl border border-dashed border-border bg-card px-5 py-14 text-center">
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/5 text-cyan-700 dark:text-primary">
                  <PackageCheck className="h-7 w-7" aria-hidden="true" />
                </div>
                <h2 className="text-base font-semibold">
                  {search || activeFilters.length
                    ? view === 'needs_method'
                      ? 'No matching sales'
                      : 'No matching shipments'
                    : view === 'needs_method'
                      ? 'No sales need classification'
                      : 'No shipments here yet'}
                </h2>
                <p className="mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
                  {search || activeFilters.length
                    ? 'Try a different search or clear your filters to find a shipment.'
                    : view === 'needs_method'
                      ? 'Sales that need a delivery method will appear in this queue.'
                      : 'New Courier and Local delivery sales appear here automatically.'}
                </p>
                {(search || activeFilters.length > 0) && (
                  <button type="button" className={`${deliveryButton} mt-5`} onClick={resetFilters}>
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <>
                <div
                  aria-hidden="true"
                  className="hidden grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)_minmax(0,.85fr)_auto] gap-5 px-4 text-[10px] font-semibold uppercase tracking-[.12em] text-muted-foreground xl:grid"
                >
                  <span className="pl-[52px]">Sale / customer</span>
                  <span>Courier / tracking</span>
                  <span>Status</span>
                  <span className="w-[140px] text-right">Actions</span>
                </div>
                <ul className="space-y-3">
                  {rows.map((row) => (
                    <DeliveryShipmentRow
                      key={row.sale_id}
                      row={row}
                      selected={selected.some((r) => r.sale_id === row.sale_id)}
                      selectionDisabled={
                        selecting ||
                        (!selected.some((r) => r.sale_id === row.sale_id) && selected.length >= 100)
                      }
                      onToggle={() => toggle(row)}
                      onUpdate={
                        can('sales.status') && row.delivery_id
                          ? () => open('update', row)
                          : undefined
                      }
                      onWhatsApp={
                        can('sales.whatsapp') && row.delivery_id
                          ? () => open('whatsapp', row)
                          : undefined
                      }
                    />
                  ))}
                </ul>
              </>
            )}
            <div className="flex items-center justify-between gap-3 border-t border-border pt-4 text-xs">
              <button
                className={deliveryButton}
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                Previous
              </button>
              <span className="text-muted-foreground">
                Page <strong className="font-semibold text-foreground">{page + 1}</strong> of{' '}
                {Math.max(1, Math.ceil((query.data?.total || 0) / 50))}
              </span>
              <button
                className={deliveryButton}
                disabled={(page + 1) * 50 >= (query.data?.total || 0)}
                onClick={() => setPage(page + 1)}
              >
                Next
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </section>
        </>
      )}
      {!!selected.length && (
        <SelectedSalesActionDock anchor={anchor}>
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:gap-4">
              <div className="flex items-center gap-2">
                <span className="flex h-7 min-w-7 items-center justify-center rounded-lg bg-primary/10 px-1.5 text-xs font-semibold tabular-nums text-cyan-700 dark:text-primary">
                  <Check className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
                <strong className="text-xs font-medium sm:text-sm">
                  {selected.length} selected
                </strong>
              </div>
              <button
                className="min-h-11 px-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => setSelected([])}
              >
                Clear selection
              </button>
            </div>
            <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:justify-end">
              {can('sales.status') && view !== 'needs_method' && (
                <button
                  className={cn(
                    deliveryButton,
                    'flex-1 border-transparent bg-primary text-primary-foreground hover:bg-primary/90 sm:flex-none',
                  )}
                  aria-label="Update status"
                  onClick={() => open('update')}
                >
                  <PackageCheck className="h-4 w-4" aria-hidden="true" />
                  <span className="sm:hidden">Update</span>
                  <span className="hidden sm:inline">Update status</span>
                </button>
              )}
              {can('sales.whatsapp') && view !== 'needs_method' && (
                <button
                  className={`${deliveryButton} w-11 shrink-0 px-0 sm:w-auto sm:px-3`}
                  aria-label="WhatsApp updates"
                  onClick={() => open('whatsapp')}
                >
                  <MessageCircle className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden sm:inline">WhatsApp updates</span>
                </button>
              )}
              <DeliveryDocuments rows={selected} compact />
            </div>
          </div>
        </SelectedSalesActionDock>
      )}
      {dialog === 'update' && (
        <DeliveryUpdateDialog rows={frozen} onClose={() => setDialog(null)} onComplete={complete} />
      )}
      {dialog === 'whatsapp' && (
        <DeliveryWhatsAppDialog rows={frozen} onClose={() => setDialog(null)} />
      )}
    </div>
  );
}
